// ─── aiResultsExplanation.js ────────────────────────────────────────────────
// « Document résultats » — génération d'un petit mot CLIENT-FACING qui explique
// à la cliente ce que montrent ses analyses, dans la voix d'Anissa. Ce texte
// alimente un Word DÉDIÉ et AUTONOME (pas le plan, pas l'app cliente).
//
// Différence avec suggestGlobalSynthesis (qui produit une synthèse INTERNE pour
// Anissa) : ici on s'adresse directement à la cliente, ton chaleureux et
// tutoiement, comme la lettre d'intro. Même discipline clinique stricte.
//
// GARDE-FOUS (repris de suggestGlobalSynthesis) :
// - Registre NUTRITIONNISTE simple : pas de diagnostic, pas de maladie, pas de
//   traitement, pas de jargon labo sauf si Anissa l'a elle-même écrit.
// - Consolide UNIQUEMENT ce qu'Anissa a saisi. N'invente RIEN (aucune valeur,
//   aucun marqueur, aucun résultat absent des données fournies).
// - Ton prudent : « semble aller vers », « à confirmer », « on va soutenir ».
// - Disclaimer nutritionnel intégré en fin de texte (obligatoire).
// - Sortie = BROUILLON pré-rempli dans un textarea. Jamais envoyé ni sauvegardé
//   automatiquement : Anissa relit, édite, valide, puis télécharge le Word.
// - Aucune décision clinique / seuil codé en dur : le prompt cadre le périmètre.
//
// Réutilise le client Claude unifié (services/anthropic.js — jamais d'appel
// direct à /api/claude, aucun modèle codé en dur).

import { callClaude, ClaudeApiError } from './anthropic';

export const EXTERNAL_RESULTS_EXPLANATION_PROMPT_FILENAME =
  'prompt-explication-resultats-cliente.txt';

// Disclaimer nutritionnel obligatoire, intégré en fin de texte. Formulation
// simple, gardée constante (testée). Anissa peut l'ajuster après coup dans le
// textarea, mais la proposition IA le contient toujours.
export const RESULTS_DISCLAIMER =
  'Cette lecture est nutritionnelle et ne remplace pas un avis médical.';

/**
 * Pack à copier dans la conversation ChatGPT où Anissa a déjà joint les PDF.
 * Le SaaS ne transmet ici aucun PDF ni résultat : il fournit uniquement le
 * profil minimisé relu par Anissa et un contrat de rédaction client-facing.
 */
export function buildExternalResultsExplanationPrompt(profileSummary = '') {
  const summary = String(profileSummary ?? '').replace(/\r\n/g, '\n').trim();
  return [
    'PACK DE TRAVAIL — EXPLICATION DES RÉSULTATS À LA CLIENTE',
    '',
    'MISSION',
    'Les PDF du laboratoire sont déjà joints dans cette conversation. À partir de ces documents et du profil factuel ci-dessous, rédige une synthèse courte qui explique directement à la cliente les points essentiels de ses résultats, dans la voix d’Anissa.',
    'Ce document est distinct du plan alimentaire. Anissa va le relire, le modifier si nécessaire, puis l’exporter en Word depuis son SaaS.',
    '',
    'VOIX D’ANISSA',
    '- Écris en français, au tutoiement, avec un ton humain, calme, pédagogique et rassurant.',
    '- Parle à la première personne lorsque cela est naturel : « je vois », « je souhaite t’expliquer », « nous allons suivre ».',
    '- Explique les termes techniques avec des mots simples sans infantiliser la cliente.',
    '- Évite le jargon, les formulations mécaniques et les précautions répétitives.',
    '- Écris comme Anissa parlerait pendant un rendez-vous : phrases naturelles, directes et concrètes. Pas de ton scolaire, froid ou artificiel.',
    '- Ne répète pas la même idée dans plusieurs parties et ne fais pas de longue introduction.',
    '- Adopte une lecture de nutrition fonctionnelle : considère le dossier dans son ensemble et cherche les liens utiles entre les résultats, le terrain, le ressenti et les habitudes de vie.',
    '- Présente ces liens comme des pistes de compréhension, jamais comme des diagnostics, des causes certaines ou des promesses de résultat.',
    '',
    'RÈGLES DE FIDÉLITÉ',
    '- Utilise uniquement les informations réellement présentes dans les PDF et dans le profil ci-dessous.',
    '- N’invente aucun résultat, aucune valeur, aucune unité, aucune date, aucune cause et aucun lien de causalité.',
    '- Ne pose aucun diagnostic et ne propose aucune modification de traitement médical.',
    '- Conserve exactement les chiffres importants, leurs unités, la date ou période et le type de rapport ou la page lorsque ces éléments sont lisibles.',
    '- Pour l’ADN, conserve les génotypes ou libellés exacts utiles et explique simplement ce qu’ils peuvent éclairer, sans déterminisme génétique.',
    '- Pour le cortisol salivaire, décris la courbe et les différents moments de prélèvement sans déduire une maladie.',
    '- Traite également tous les autres rapports joints. Ne laisse aucun document de côté.',
    '- Si deux documents divergent, présente les deux informations sans choisir arbitrairement.',
    '- Si une information est absente, illisible ou incertaine, écris : « Point à vérifier avec Anissa : … »',
    '- Ne recopie aucune adresse, téléphone, e-mail, identifiant, nom de tiers ou nom de fichier identifiant.',
    '',
    'FORMAT COURT OBLIGATOIRE',
    '- Réponds uniquement avec le document final. Maximum 550 mots.',
    '- N’utilise aucun dièse (#), aucun titre Markdown, aucun astérisque décoratif et aucun bloc de code.',
    '- Utilise les titres simples ci-dessous, écrits tels quels sur leur propre ligne.',
    '- Sous chaque titre, écris des phrases courtes et au maximum 2 ou 3 puces utiles.',
    '',
    'CE QUE JE RETIENS DE TON DOSSIER',
    'En 3 ou 4 phrases maximum, donne la vue d’ensemble fonctionnelle : les principaux points forts, les points plus fragiles et les liens prudents avec le ressenti, les habitudes ou les objectifs réellement déclarés.',
    '',
    'ADN',
    'Présente uniquement les éléments ADN utiles. Sépare clairement : Points forts ; Points à surveiller ; À suivre ou à vérifier. Conserve les génotypes ou chiffres importants et explique-les simplement.',
    '',
    'SALIVAIRE',
    'Présente uniquement le cortisol salivaire et les autres données salivaires. Sépare clairement : Points forts ; Points à surveiller ; À suivre ou à vérifier. Garde les heures, valeurs et unités importantes lorsqu’elles sont lisibles.',
    '',
    'SANGUIN',
    'Présente les analyses sanguines et, s’il existe un autre rapport qui n’est ni ADN ni salivaire, ajoute-le ici sous un libellé simple et distinct. Sépare clairement : Points forts ; Points à surveiller ; À suivre ou à vérifier. Garde les valeurs et unités réellement utiles à la compréhension.',
    '',
    'LES POINTS À SUIVRE ENSEMBLE',
    'Termine par 3 à 5 points maximum : les priorités qui ressortent de la lecture fonctionnelle globale, ce que nous allons observer, ce qui mérite une vérification et ce qui sera pris en compte dans l’accompagnement alimentaire. N’écris ni menu, ni protocole, ni prescription.',
    '',
    'Si une des trois familles de documents n’est pas présente ou n’est pas lisible, conserve son titre et écris une seule phrase factuelle : « Aucun résultat exploitable dans les documents transmis. »',
    '',
    'CONCLUSION',
    'Termine par une seule phrase naturelle dans la voix d’Anissa, puis par cette phrase exacte, sur sa propre ligne :',
    `« ${RESULTS_DISCLAIMER} »`,
    '',
    'PROFIL FACTUEL RELU — SANS IDENTITÉ DIRECTE',
    '<<< DÉBUT DU PROFIL >>>',
    summary || 'Aucune information exploitable renseignée dans le profil.',
    '<<< FIN DU PROFIL >>>',
  ].join('\n');
}

// Libellés lisibles des statuts cliniques (miroir de STATUSES dans
// ClientJourneyPage.jsx). Rend le statut saisi par Anissa lisible dans le
// prompt — aucune logique clinique ici.
const STATUS_LABELS = {
  optimal: 'Optimal',
  surveiller: 'À surveiller',
  sous_optimal: 'Sous-optimal',
  eleve: 'Élevé',
  non_interpretable: 'Non interprétable',
};

const SYSTEM_PROMPT = `Tu écris avec la voix d'Anissa Deroubaix pour son accompagnement nutritionnel (Anissa n'est ni médecin ni biologiste). Tu rédiges un petit mot CHALEUREUX adressé directement à sa cliente pour lui expliquer, dans un langage simple, ce que montrent ses analyses et ce qu'on va faire ensemble dans l'assiette.

C'est un BROUILLON. Anissa le relit, l'ajuste et le valide avant de l'envoyer. Reste fidèle à ce qu'elle a saisi.

TON — CHALEUREUX ET DIRECT (comme une lettre à la cliente)
- Tutoiement OBLIGATOIRE ("tu", "ton", "ta", "tes"). Jamais "vous".
- Anissa parle à la 1re personne ("je vois dans tes analyses", "je te propose", "on va soutenir").
- Chaleureux et rassurant, mais sobre : pas de marketing, pas de pathos, pas d'exclamations à répétition.
- La cliente doit se sentir vue et accompagnée, jamais jugée ni inquiétée inutilement.

REGISTRE — LANGAGE NUTRITIONNISTE SIMPLE (le point le plus important)
- Écris comme une nutritionniste parle, pas comme un compte-rendu de laboratoire ni un médecin.
- Traduis toujours en concret : ce que la cliente RESSENT (fatigue l'après-midi, digestion lente, ballonnements, transit paresseux) et ce qu'on va faire dans l'ASSIETTE et le quotidien.
- Phrases courtes et accessibles.

INTERDIT — JARGON MÉDICAL / BIOLOGIQUE
- Pas de termes techniques de labo : noms de bactéries (Firmicutes, Bacteroidetes, Akkermansia…), ratios, "profil hormonal", "en bas de norme", noms de marqueurs chiffrés.
- SEULE EXCEPTION : si Anissa a elle-même écrit un de ces termes dans sa lecture, tu peux le reprendre tel quel. Sinon, exprime-le en mots simples ("l'équilibre de ta flore intestinale à soutenir", "ton énergie qui retombe l'après-midi").
- Tu ne poses JAMAIS de diagnostic, tu ne nommes aucune maladie, aucun traitement ni dosage médicamenteux.

CE QUE TU AS LE DROIT DE FAIRE
- Reformuler chaleureusement, pour la cliente, UNIQUEMENT ce qu'Anissa a fourni (valeurs, ses lectures, statuts).
- Dégager 2 ou 3 priorités nutritionnelles concrètes qui ressortent de CE qu'elle a écrit, et expliquer ce qu'on va faire ensemble.

INTERDICTIONS ABSOLUES
- N'invente AUCUNE valeur, AUCUN marqueur, AUCUN résultat absent des données fournies. Si une donnée manque, ne la suppose pas.
- Ne recopie pas ligne à ligne les valeurs brutes : tu EXPLIQUES en langage simple.
- Ne promets pas de résultat, pas de date ("en 4 semaines tu…"). Plutôt "au fil des semaines".

TON (défaut prudent)
- Formulations prudentes : "ça semble aller vers", "on va plutôt soutenir", "c'est une piste qu'on va suivre ensemble", "à confirmer". Jamais de certitude sur une cause.
- Vocabulaire d'accompagnement : "soutenir", "favoriser", "prioriser", "rééquilibrer". Évite "soigne", "guérit", "traite", "détecte".

DISCLAIMER OBLIGATOIRE (fin du texte)
- Termine TOUJOURS par cette phrase, telle quelle, sur sa propre ligne : "${RESULTS_DISCLAIMER}"

FORMAT
- Français, ~8 à 15 lignes (un vrai petit mot, pas 3 lignes). Prose continue, tu peux utiliser de courts paragraphes.
- Réponds UNIQUEMENT avec le texte du mot (disclaimer inclus), sans titre, sans préambule ("Voici…"), sans markdown, sans guillemets englobants, sans signature.`;

/**
 * Normalise l'entrée : accepte soit { fromPlan[], external[], globalSynthesis }
 * (comme suggestGlobalSynthesis), soit la forme brute results_data
 * { from_plan[], external[], global_synthesis }. Renvoie une forme canonique.
 *
 * @param {object} data
 * @returns {{ fromPlan: Array, external: Array, globalSynthesis: string }}
 */
function normalizeInput(data = {}) {
  const fromPlan = Array.isArray(data.fromPlan)
    ? data.fromPlan
    : Array.isArray(data.from_plan)
      ? data.from_plan
      : [];
  const external = Array.isArray(data.external) ? data.external : [];
  const globalSynthesis =
    (typeof data.globalSynthesis === 'string' && data.globalSynthesis) ||
    (typeof data.global_synthesis === 'string' && data.global_synthesis) ||
    '';
  return { fromPlan, external, globalSynthesis };
}

/**
 * Construit le message utilisateur (les analyses saisies + prénom) envoyé à
 * l'IA. Pure : ne dépend d'aucun I/O. Testable isolément.
 *
 * @param {object} data
 * @param {Array}  [data.fromPlan]        - analyses prescrites : { test_name, test_code, value, synthesis, category, status, markers[] }
 * @param {Array}  [data.external]        - analyses externes : { name, value, synthesis, category, status }
 * @param {string} [data.globalSynthesis] - synthèse interne éventuelle (aide au contexte, jamais recopiée telle quelle)
 * @param {string} [data.prenom]          - prénom de la cliente (pour l'adresse)
 * @returns {string} le prompt utilisateur
 */
export function buildResultsExplanationPrompt(data = {}) {
  const { fromPlan, external, globalSynthesis } = normalizeInput(data);
  const prenom = typeof data.prenom === 'string' ? data.prenom.trim() : '';

  const lines = [];

  const renderStatus = (status) => (status ? STATUS_LABELS[status] || status : null);

  const renderEntry = (label, value, synthesis, category, status, markers) => {
    const block = [`[${label}]`];
    if (category) block.push(`Catégorie : ${category}`);
    if (status) block.push(`Statut clinique (Anissa) : ${renderStatus(status)}`);
    if (value) block.push(`Valeurs saisies : ${value}`);
    if (synthesis) block.push(`Lecture clinique (Anissa) : ${synthesis}`);
    if (Array.isArray(markers) && markers.length) {
      const filled = markers.filter((m) => m && (m.value || m.synthesis));
      filled.forEach((m) => {
        const parts = [`  · ${m.label || m.marker_code}`];
        if (m.value) parts.push(`= ${m.value}${m.unit ? ` ${m.unit}` : ''}`);
        if (m.status) parts.push(`(${renderStatus(m.status)})`);
        block.push(parts.join(' '));
        if (m.synthesis) block.push(`    Note : ${m.synthesis}`);
      });
    }
    return block.join('\n');
  };

  if (prenom) {
    lines.push('=== CLIENTE ===');
    lines.push(`Prénom : ${prenom}`);
    lines.push('');
  }

  const prescribed = fromPlan.filter(
    (r) =>
      r &&
      (r.value ||
        r.synthesis ||
        (Array.isArray(r.markers) && r.markers.some((m) => m && (m.value || m.synthesis)))),
  );
  if (prescribed.length) {
    lines.push('=== ANALYSES PRESCRITES (saisies par Anissa) ===');
    prescribed.forEach((r) => {
      lines.push(
        renderEntry(
          r.test_name || r.test_code || 'Analyse',
          r.value,
          r.synthesis,
          r.category,
          r.status,
          r.markers,
        ),
      );
      lines.push('');
    });
  }

  // Cohérent avec suggestGlobalSynthesis (V97.125) : une analyse externe où
  // Anissa a saisi des valeurs/lecture compte même sans titre. Libellé de repli
  // neutre « Analyse externe » (aucune interprétation clinique ajoutée).
  const ext = external.filter((r) => r && (r.value || r.synthesis));
  if (ext.length) {
    lines.push('=== ANALYSES EXTERNES (déjà détenues par la cliente) ===');
    ext.forEach((r) => {
      lines.push(renderEntry(r.name || 'Analyse externe', r.value, r.synthesis, r.category, r.status));
      lines.push('');
    });
  }

  if (globalSynthesis && globalSynthesis.trim()) {
    lines.push('=== SYNTHÈSE INTERNE D\'ANISSA (contexte, NE PAS recopier) ===');
    lines.push(globalSynthesis.trim());
    lines.push('');
  }

  lines.push('=== TÂCHE ===');
  lines.push(
    "Rédige un petit mot chaleureux (~8-15 lignes) adressé directement à la cliente (tutoiement), en langage SIMPLE de nutritionniste (pas de jargon de labo, pas de noms de marqueurs sauf si Anissa les a écrits). Explique ce que montrent ses analyses et ce qu'on va faire ensemble dans l'assiette et le quotidien, en dégageant 2-3 priorités concrètes. Consolide UNIQUEMENT ces éléments, n'invente rien, reste prudente. Termine par la phrase de disclaimer exacte sur sa propre ligne.",
  );

  return lines.join('\n');
}

/**
 * Indique s'il y a assez de matière saisie pour proposer une explication.
 * Utilisé par l'UI pour désactiver le bouton / afficher un message.
 * Miroir de hasEnoughAnalysesForSynthesis (une synthèse interne seule NE suffit
 * PAS : il faut au moins une analyse saisie, pour ne rien inventer).
 *
 * @param {object} data - { fromPlan[]/from_plan[], external[] }
 * @returns {boolean}
 */
export function hasEnoughForResultsExplanation(data = {}) {
  const { fromPlan, external } = normalizeInput(data);
  const hasContent = (r) =>
    r &&
    (r.value ||
      r.synthesis ||
      (Array.isArray(r.markers) && r.markers.some((m) => m && (m.value || m.synthesis))));
  return fromPlan.some(hasContent) || external.some((r) => r && hasContent(r));
}

/**
 * Appelle Claude pour proposer un BROUILLON de mot client-facing expliquant les
 * analyses. Ne sauvegarde rien : renvoie juste le texte proposé, que l'UI
 * pré-remplit dans le textarea qu'Anissa édite ensuite.
 *
 * @param {object} data - { fromPlan[]/from_plan[], external[], globalSynthesis/global_synthesis, prenom }
 * @returns {Promise<string>} le brouillon d'explication
 * @throws {Error} message clair si pas de matière / erreur IA / réponse vide
 */
export async function suggestResultsExplanation(data = {}) {
  if (!hasEnoughForResultsExplanation(data)) {
    throw new Error(
      "Saisis d'abord au moins une analyse (valeur ou lecture clinique) avant de demander une proposition.",
    );
  }

  const user = buildResultsExplanationPrompt(data);

  let text;
  try {
    text = await callClaude({
      system: SYSTEM_PROMPT,
      user,
      maxTokens: 1200,
      // Filet de sécurité compliance en plus du guard vocabulaire injecté par défaut.
      sanitizeOutput: true,
    });
  } catch (err) {
    if (err instanceof ClaudeApiError) {
      throw new Error(`La proposition a échoué : ${err.message}`);
    }
    throw new Error(`La proposition a échoué : ${err?.message || err}`);
  }

  const draft = (text || '').trim();
  if (!draft) {
    throw new Error("La préparation n’a renvoyé aucune proposition. Réessaie.");
  }
  return draft;
}
